import {
  buildAssistantThreadPath,
  PROJECT_LEVEL_ROUTE_PATHS,
  parseAssistantThreadIdFromPath,
} from "@repo/shared";
import { ChevronRight } from "lucide-react";
import { type RefObject, useRef } from "react";
import { ClaudiaIcon } from "@/components/claudia-icon";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { UnreadDot } from "@/components/ui/unread-dot";
import { cn } from "@/lib/utils";
import type { AssistantConversationListItem } from "../../types";
import { ConversationList } from "./conversation-list";

/** The design's flyout width. It is the point of the surface: it fits the reason line. */
const FLYOUT_WIDTH_CLASS = "w-[340px]";

/**
 * The right edge of the Claudia nav row: the blue dot, then the chevron.
 *
 * The dot means "something in this project is waiting for you" and is the whole reason the row
 * carries anything at all — it is the only place that fact is visible from every screen. The
 * chevron says the row opens a flyout rather than navigating, so it appears on hover and stays
 * up while the row is current or its flyout is open; a permanent chevron would read as a
 * disclosure triangle on a row that has no children. A row that navigates instead — a project
 * with no conversation goes straight to a new chat (LAT-1077) — carries no chevron at all.
 */
export function ConversationsNavTrailing({
  needsYou,
  opensFlyout,
  showChevron,
}: {
  needsYou: boolean;
  opensFlyout: boolean;
  showChevron: boolean;
}) {
  return (
    <>
      {needsYou ? (
        <span data-testid="claudia-nav-needs-you-dot" className="flex shrink-0 items-center">
          <UnreadDot active />
        </span>
      ) : null}
      {opensFlyout ? (
        <ChevronRight
          aria-hidden="true"
          className={cn(
            "size-3.5 shrink-0 text-neutral-dim transition-opacity",
            showChevron ? "opacity-100" : "opacity-0 group-hover/tree-node:opacity-100"
          )}
        />
      ) : null}
    </>
  );
}

export interface ConversationsFlyoutPanelProps {
  items: readonly AssistantConversationListItem[];
  isLoading: boolean;
  /** The project path the tab is on; a thread route names the row to mark as current. */
  currentPath: string;
  onNavigate: (path: string) => void;
  onClose: () => void;
}

/**
 * The flyout's contents, without the popover around them.
 *
 * Split out from `ConversationsFlyout` so the panel can be rendered — and tested — on its own:
 * everything this surface decides (which row is current, where a row goes, when it closes)
 * lives here, and the popover above it only positions it.
 */
export function ConversationsFlyoutPanel({
  items,
  isLoading,
  currentPath,
  onNavigate,
  onClose,
}: ConversationsFlyoutPanelProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const currentThreadId = parseAssistantThreadIdFromPath(currentPath);

  const go = (path: string) => {
    onNavigate(path);
    onClose();
  };

  /**
   * Up and Down move between conversation rows. The flyout hangs off main navigation, so it has
   * to be reachable the way navigation is; the rows are ordinary buttons, so Enter needs nothing
   * of its own and Escape belongs to the popover.
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }
    const rows = listRef.current
      ? Array.from(listRef.current.querySelectorAll<HTMLButtonElement>("button"))
      : [];
    if (rows.length === 0) {
      return;
    }
    event.preventDefault();
    const index = rows.indexOf(document.activeElement as HTMLButtonElement);
    const nextIndex =
      event.key === "ArrowDown"
        ? index < 0
          ? 0
          : Math.min(index + 1, rows.length - 1)
        : index < 0
          ? rows.length - 1
          : Math.max(index - 1, 0);
    rows[nextIndex]?.focus();
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: roving arrow keys over the rows below; the rows are buttons and keep their own Enter/Space
    <div className="flex flex-col" onKeyDown={handleKeyDown}>
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
        <span className="flex min-w-0 items-center gap-2">
          <ClaudiaIcon className="size-3.5 shrink-0" />
          <span className="truncate text-[13px] font-semibold text-foreground">Claudia</span>
        </span>
        <button
          type="button"
          onClick={() => go(PROJECT_LEVEL_ROUTE_PATHS.aiAssistantNewChat)}
          className="shrink-0 text-[12px] font-medium text-primary transition-colors hover:underline"
        >
          New chat
        </button>
      </div>

      <div ref={listRef} className="max-h-[380px] min-h-0 overflow-y-auto">
        {isLoading ? (
          <ConversationsFlyoutLoading />
        ) : (
          <ConversationList
            items={items}
            currentThreadId={currentThreadId}
            density="default"
            onSelect={(threadId) => go(buildAssistantThreadPath(threadId))}
            emptyLabel="No conversations yet"
          />
        )}
      </div>

      <div className="border-t border-border px-3 py-2.5">
        <button
          type="button"
          onClick={() => go(PROJECT_LEVEL_ROUTE_PATHS.aiAssistantHistory)}
          className="text-[12px] font-medium text-primary transition-colors hover:underline"
        >
          View all conversations
        </button>
      </div>
    </div>
  );
}

/**
 * Three row-shaped placeholders rather than a spinner: the flyout is opened to *look* at a list,
 * and a box that changes height under the pointer is worse than one that does not.
 */
function ConversationsFlyoutLoading() {
  return (
    <div className="flex flex-col gap-3 px-3 py-3" aria-busy="true">
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex flex-col gap-1.5">
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

/**
 * The conversations flyout: a 340px panel anchored to the Claudia nav row.
 *
 * It is a `Popover` with no `Trigger` — the trigger is a tree row that the sidebar owns, so the
 * open state and the anchor are both passed in. `finalFocus` returns focus to that row on a
 * keyboard close (Escape), and deliberately does not on a pointer close, where focus belongs
 * wherever the person just clicked.
 */
export function ConversationsFlyout({
  open,
  onOpenChange,
  anchorRef,
  items,
  isLoading,
  currentPath,
  onNavigate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef: RefObject<HTMLElement | null>;
  items: readonly AssistantConversationListItem[];
  isLoading: boolean;
  currentPath: string;
  onNavigate: (path: string) => void;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverContent
        anchor={anchorRef}
        side="right"
        align="start"
        sideOffset={6}
        aria-label="Conversations"
        finalFocus={(closeType) => (closeType === "keyboard" ? anchorRef.current : false)}
        className={cn(
          FLYOUT_WIDTH_CLASS,
          "gap-0 rounded-none border border-border bg-popover p-0 text-[13px] shadow-md ring-0"
        )}
      >
        <ConversationsFlyoutPanel
          items={items}
          isLoading={isLoading}
          currentPath={currentPath}
          onNavigate={onNavigate}
          onClose={() => onOpenChange(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
